import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { resolveApiMessage, showToast } from '../../../helpers';
import {
  assignZoomRoom,
  releaseZoomRoom,
  TZoomRoomOptions,
} from '../../../requests/opsRecords';
import { TOpsClassRelatedEx } from '../../../requests/opsRegistrationsExtra';
import { btnGhost, btnSecondary, Card, inputClass } from '../ui';

/** The class's Zoom room: pick a free one, or release it. Not written to GoMeddo. */
const ZoomRoomsCard: React.FC<{
  id: string;
  related: TOpsClassRelatedEx;
  roomOptions?: TZoomRoomOptions;
  refetchRooms: () => unknown;
}> = ({ id, related, roomOptions, refetchRooms }) => {
  const queryClient = useQueryClient();
  type TZoomRoom = {
    id: string;
    name: string | null;
    url: string | null;
    roomCode: string | null;
    email: string | null;
  };
  const zoomRooms =
    (related as unknown as { zoomRooms?: TZoomRoom[] })?.zoomRooms ?? [];
  const [pickRoom, setPickRoom] = useState('');
  const zoomAssign = useMutation({
    mutationFn: (v: { roomId?: string; force?: boolean }) =>
      assignZoomRoom(id!, v.roomId, v.force),
    onSuccess: (r) => {
      showToast(
        r.forced
          ? `${r.room} assigned (double-booked on purpose)`
          : `${r.room} assigned`,
        'success',
      );
      refetchRooms();
      queryClient.invalidateQueries({ queryKey: ['opsClass', id] });
      queryClient.invalidateQueries({ queryKey: ['opsClassRelated', id] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });
  const zoomRelease = useMutation({
    mutationFn: () => releaseZoomRoom(id!),
    onSuccess: () => {
      showToast('Zoom room released', 'success');
      refetchRooms();
      queryClient.invalidateQueries({ queryKey: ['opsClass', id] });
      queryClient.invalidateQueries({ queryKey: ['opsClassRelated', id] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  return (
    <Card title={`Zoom Rooms · ${zoomRooms.length}`}>
      {roomOptions && (
        <div className="border-b border-white-regular px-5 py-3 text-sm">
          <div className="mb-2 text-xs text-gray-bold">
            {roomOptions.free} of {roomOptions.items.length} rooms free for this
            class (±{roomOptions.window.bufferMinutes} min). Assigning records
            the room here; it is not written to GoMeddo.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={inputClass}
              value={pickRoom}
              onChange={(e) => setPickRoom(e.target.value)}
            >
              <option value="">Lowest free room</option>
              {roomOptions.items.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.busy
                    ? ` — taken by ${r.busy.by}`
                    : r.current
                    ? ' — current'
                    : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={btnSecondary}
              disabled={zoomAssign.isPending}
              onClick={() =>
                zoomAssign.mutate({ roomId: pickRoom || undefined })
              }
            >
              Assign room
            </button>
            {roomOptions.currentRoomId && (
              <button
                type="button"
                className={btnGhost}
                disabled={zoomRelease.isPending}
                onClick={() => zoomRelease.mutate()}
              >
                Release
              </button>
            )}
          </div>
        </div>
      )}
      <div className="space-y-1.5 p-5 text-sm">
        {zoomRooms.length === 0 ? (
          <span className="text-gray-bold">
            No room yet. Pick one above, or leave it for the scheduler.
          </span>
        ) : (
          zoomRooms.map((z) => (
            <div key={z.id}>
              <b className="text-gray-four">{z.name ?? 'Zoom room'}</b>
              <span className="text-xs text-gray-bold">
                {' · ' +
                  [z.roomCode ? `code ${z.roomCode}` : null, z.email]
                    .filter(Boolean)
                    .join(' · ')}
              </span>
              {z.url && (
                <div className="truncate text-xs">
                  <a
                    className="text-blue-primary hover:underline"
                    href={z.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {z.url}
                  </a>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

export default ZoomRoomsCard;
